// API for the Mobile App Codes page (Admin / IT): texts (ClickSend) or
// emails (noreply@) Node Mobile download codes. Server:
// server/routes/api/AppCodes/appCodesHandler.js. Every call identifies the
// caller by the x-user-id header (no token validation server-side — the
// same trust model as the Safety and Fleet admin routes). Scripts use the
// same /send endpoint with ?key= instead.

export const APP_CODES_ROLES = ["Admin", "IT"];

const request = async (url, userId, { method = "GET", body } = {}) => {
  const res = await fetch(url, {
    method,
    headers: {
      Accept: "application/json",
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      "x-user-id": userId || "",
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  let content = {};
  try {
    content = await res.json();
  } catch (e) {
    // Non-JSON error page (e.g. the server is restarting).
  }
  if (!res.ok) throw new Error(content.error || "Request failed");
  return content;
};

// { counts, batches, sends, templates, defaults, config }
export const fetchAppCodes = (userId) => request("/api/appcodes", userId);

// Someone who already got a code gets the same one again unless `fresh`.
export const sendAppCode = (userId, to, { fresh = false } = {}) =>
  request("/api/appcodes/send", userId, { method: "POST", body: { to, fresh } });

export const saveAppCodeTemplates = (userId, templates) =>
  request("/api/appcodes/templates", userId, { method: "PUT", body: templates });

// sheet: { codes: [{ code, link }], orderId, product } → { added, skipped }
export const importAppCodes = (userId, sheet) =>
  request("/api/appcodes/import", userId, { method: "POST", body: sheet });

// Mirror of the server's recipient check, for the live "we'll text / we'll
// email" hint as someone types. The server has the final say.
export const describeRecipient = (raw) => {
  const value = String(raw || "").trim();
  if (!value) return null;
  if (value.includes("@")) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
      ? { channel: "email", display: value.toLowerCase() }
      : { error: "Finish typing the email address" };
  }
  const digits = value.replace(/\D/g, "");
  if (!/[a-z]/i.test(value)) {
    const us = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
    if (!value.startsWith("+") && us.length === 10) {
      return { channel: "sms", display: `(${us.slice(0, 3)}) ${us.slice(3, 6)}-${us.slice(6)}` };
    }
    if (value.startsWith("+") && digits.length >= 8 && digits.length <= 15) {
      return { channel: "sms", display: `+${digits}` };
    }
  }
  return { error: "Enter a 10-digit phone number or an email address" };
};

export const CHANNEL_LABEL = { sms: "Text", email: "Email" };

// What {link} and {code} look like in the template previews. {link} is the
// server's tracked link (it forwards to the App Store), not Apple's own URL.
export const SAMPLE_CODE = {
  code: "ABCD1234EFGH",
  link: "https://node.bigclumber.com/api/appcodes/go/Xk7Qp2Lm9a",
};

export const fillTemplate = (template, sample = SAMPLE_CODE) =>
  String(template || "")
    .replace(/\{\s*link\s*\}/gi, sample.link)
    .replace(/\{\s*code\s*\}/gi, sample.code);

// Rough number of texts a message bills as: 160 characters fit in one,
// 153 per part once it splits. Anything outside plain ASCII (emoji, curly
// quotes) switches the whole message to 70 / 67.
export const smsParts = (text) => {
  const value = String(text || "");
  if (/^[\n\r\x20-\x7E]*$/.test(value)) {
    // ^ { } [ ] ~ \ | take two slots each.
    const length = value.length + (value.match(/[\^{}[\]~\\|]/g) || []).length;
    return { length, parts: length <= 160 ? 1 : Math.ceil(length / 153) };
  }
  const length = [...value].length;
  return { length, parts: length <= 70 ? 1 : Math.ceil(length / 67), unicode: true };
};
